/**
 * Shared wizard assist utilities (unsure inputs, references, agent delegation).
 */
import { isMetadataXmlContent, isMetadataUrl } from './vysorMetadata';

export const UNSURE_PHRASES = [
  'not sure',
  'unsure',
  'idk',
  "i don't know",
  'dont know',
  'not-sure',
  'no idea',
  'not certain',
  "don't know",
  'no clue',
];

export function isUnsureInput(raw: string): boolean {
  const norm = raw.trim().toLowerCase().replace(/[!?.]+$/g, '').trim();
  if (!norm) return false;
  if (UNSURE_PHRASES.includes(norm)) return true;
  if (
    /^(i\s+)?(am\s+|'?m\s+)?(still\s+)?(not\s+sure|unsure)\b/i.test(norm) ||
    /^(i\s+)?(do\s+not|don'?t)\s+(really\s+)?know\b/i.test(norm) ||
    /^(i\s+have\s+)?no\s+(idea|clue)\b/i.test(norm) ||
    /^(not\s+certain|no\s+preference|either\s*(one)?|whatever)\b/i.test(norm)
  ) {
    return true;
  }
  if (norm.length <= 48 && /\b(not sure|unsure|idk|no clue|no idea|don'?t know)\b/i.test(norm)) {
    return true;
  }
  return false;
}

/** Exact chip-like answers that should advance the wizard, not call the model. */
export function isExactWizardShortcut(raw: string): boolean {
  return /^(saml|oidc|saml2|openid|yes|no|yep|yeah|ok|okay|skip|cancel|not sure|unsure|idk)$/i.test(
    raw.trim().replace(/[!?.]+$/g, ''),
  );
}

export function isHelpSeekingInput(raw: string): boolean {
  const t = raw.trim();
  if (!t) return false;
  if (isUnsureInput(t)) return true;
  if (/\?/.test(t) && t.length >= 4) return true;
  if (
    /^(help|which|what|why|how|tell me|explain|compare|difference|recommend|suggest|confused|hmm+)\b/i.test(
      t,
    )
  ) {
    return true;
  }
  if (
    /\b(help me|not sure|don't know|do not know|which one|what should|how do|i'?m stuck|i am stuck|this is confusing|don'?t understand)\b/i.test(
      t,
    )
  ) {
    return true;
  }
  return false;
}

export function isConversationalAssistInput(raw: string): boolean {
  const t = raw.trim();
  if (!t) return false;
  if (isExactWizardShortcut(t)) return false;
  if (isHelpSeekingInput(t)) return true;
  if (/^https?:\/\//i.test(t) || isMetadataXmlContent(t)) return true;
  // Long free-form notes / vendor paste — ask the model
  return t.length >= 40;
}

/**
 * Mid-wizard stuck/question phrasing → talk to the model.
 * Any natural question works — not limited to "guide me" / "steps".
 */
export function looksLikeOpenAiConversation(raw: string): boolean {
  const t = raw.trim();
  if (!t || t.length < 2) return false;
  if (isExactWizardShortcut(t)) return false;
  return isHelpSeekingInput(t) || isConversationalAssistInput(t);
}

/** On assist steps: help-mode or conversational free text → model. */
export function shouldDelegateToAgent(raw: string, assistActive: boolean): boolean {
  const t = raw.trim();
  if (!t) return false;
  if (assistActive) return true;
  return isConversationalAssistInput(t);
}

export function shouldTryReferenceInference(raw: string): boolean {
  const t = raw.trim();
  if (!t || isUnsureInput(t)) return false;
  return /^https?:\/\//i.test(t) || isMetadataXmlContent(t) || isMetadataUrl(t) || t.length >= 40;
}

export async function resolveReferenceText(
  raw: string,
  fetchUrl: (url: string) => Promise<{ text: string }>,
): Promise<{ text: string; source: 'inline' | 'url' }> {
  const trimmed = raw.trim();
  if (isMetadataXmlContent(trimmed)) {
    return { text: trimmed, source: 'inline' };
  }
  if (/^https?:\/\//i.test(trimmed)) {
    const { text } = await fetchUrl(trimmed);
    return { text, source: 'url' };
  }
  return { text: trimmed, source: 'inline' };
}

export function buildAgentContextHeader(title: string, lines: string[]): string {
  return [`[VYSOR wizard — ${title}]`, ...lines, ''].join('\n');
}

/** Shared instruction: answer THIS user message dynamically — never a fixed script. */
export const DYNAMIC_AI_REPLY_RULES = [
  "Answer the user's actual words dynamically (like ChatGPT/Gemini) — not a fixed template.",
  'Do not key off specific phrases like "guide me" or "give me steps"; any stuck/confused wording is fine.',
  'Vary your reply; do not repeat the same canned explanation every time.',
  'Stay on the current wizard step — do not restart the flow.',
  'If a concrete choice helps, recommend it briefly and say what to tap next.',
].join(' ');
